import { useState, useEffect, useRef } from "react";
import { X, Expand, Download, Plus, Minus } from "lucide-react";

interface DocumentModalProps {
  src: string;
  onClose: () => void;
}

export default function DocumentModal({ src, onClose }: DocumentModalProps) {
  const [zoom, setZoom] = useState(1);
  const [fullScreen, setFullScreen] = useState(false);
  const [isImage, setIsImage] = useState(false);
  const viewerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const imgExtensions = ["jpg", "jpeg", "png", "gif", "webp", "bmp"];
    const ext = src.split(".").pop()?.toLowerCase();
    setIsImage(ext ? imgExtensions.includes(ext) : false);
  }, [src]);

  // Mouse wheel zoom
  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setZoom((z) => Math.min(Math.max(z + delta, 0.5), 3));
  };

  // Drag to pan
  useEffect(() => {
    if (!viewerRef.current || !isImage) return;
    const el = viewerRef.current;
    let isDragging = false;
    let startX = 0;
    let startY = 0;
    let scrollLeft = 0;
    let scrollTop = 0;

    const onMouseDown = (e: MouseEvent) => {
      if (zoom <= 1) return;
      isDragging = true;
      startX = e.pageX - el.offsetLeft;
      startY = e.pageY - el.offsetTop;
      scrollLeft = el.scrollLeft;
      scrollTop = el.scrollTop;
      el.style.cursor = "grabbing";
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      e.preventDefault();
      const x = e.pageX - el.offsetLeft;
      const y = e.pageY - el.offsetTop;
      const walkX = x - startX;
      const walkY = y - startY;
      el.scrollLeft = scrollLeft - walkX;
      el.scrollTop = scrollTop - walkY;
    };

    const onMouseUp = () => {
      isDragging = false;
      el.style.cursor = "grab";
    };

    el.addEventListener("mousedown", onMouseDown);
    el.addEventListener("mousemove", onMouseMove);
    el.addEventListener("mouseup", onMouseUp);
    el.addEventListener("mouseleave", onMouseUp);

    return () => {
      el.removeEventListener("mousedown", onMouseDown);
      el.removeEventListener("mousemove", onMouseMove);
      el.removeEventListener("mouseup", onMouseUp);
      el.removeEventListener("mouseleave", onMouseUp);
    };
  }, [zoom, isImage]);

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = src;
    link.download = src.split("/").pop() || "document";
    link.click();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 animate-fade-in">
      <div
        className={`relative bg-white rounded-lg shadow-lg transition-all duration-300 transform ${
          fullScreen ? "w-full h-full" : "w-11/12 max-w-4xl h-4/5"
        }`}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-700 hover:text-gray-900 transition"
          aria-label="Close"
        >
          <X className="h-6 w-6" />
        </button>

        {/* Toolbar */}
        <div className="absolute top-4 left-4 flex items-center space-x-3 bg-white/80 backdrop-blur-md rounded-full px-3 py-1 shadow-md">
          <button onClick={() => setZoom((z) => Math.min(z + 0.2, 3))} className="p-1 hover:bg-gray-200 rounded">
            <Plus className="h-5 w-5 text-gray-700" />
          </button>
          <button onClick={() => setZoom((z) => Math.max(z - 0.2, 0.5))} className="p-1 hover:bg-gray-200 rounded">
            <Minus className="h-5 w-5 text-gray-700" />
          </button>
          <button onClick={() => setFullScreen((fs) => !fs)} className="p-1 hover:bg-gray-200 rounded">
            <Expand className="h-5 w-5 text-gray-700" />
          </button>
          {/* <button onClick={handleDownload} className="p-1 hover:bg-gray-200 rounded">
            <Download className="h-5 w-5 text-gray-700" />
          </button> */}
        </div>

        {/* Viewer */}
        <div
          ref={viewerRef}
          className="w-full h-full flex items-center justify-center overflow-auto cursor-grab"
          onWheel={handleWheel}
        >
          {isImage ? (
            <img
              ref={imgRef}
              src={src}
              alt="Document"
              style={{ transform: `scale(${zoom})`, transformOrigin: "center center" }}
              className="object-contain transition-transform duration-200"
            />
          ) : (
            <iframe
              src={src}
              title="Document Preview"
              className="w-full h-full rounded-lg transition-transform duration-200"
              style={{
                border: "1px solid #e5e7eb",
                transform: `scale(${zoom})`,
                transformOrigin: "center center",
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
